import { Link } from 'react-router-dom'
import { useState } from 'react'
import { COMMISSION, calcCommission, calcDomainCommission, PRODUCTS } from '../lib/products'
import { useI18n } from '../lib/i18n'

const PLANS = [
  { label: 'Starter — €29/mo', price: 29 },
  { label: 'Pro — €59/mo', price: 59 },
  { label: 'Business — €99/mo', price: 99 },
]

function EarningsCalculator() {
  const { t } = useI18n()
  const [planIdx, setPlanIdx] = useState(1)
  const [dealsPerMonth, setDealsPerMonth] = useState(3)
  const plan = PLANS[planIdx]
  const comm = calcCommission(plan.price)
  const monthly = comm.monthly * dealsPerMonth
  const total6m = comm.total6m * dealsPerMonth

  return (
    <div className="card mt-5 border-brand-500/30 bg-gradient-to-br from-brand-500/5 to-fuchsia-500/5">
      <h2 className="text-xl font-semibold text-white">{t('🧮 Earnings calculator')}</h2>
      <p className="text-ink-500 text-sm mt-1">{t('Adjust the sliders — see your real potential.')}</p>

      <div className="mt-5 space-y-5">
        <div>
          <label className="label block mb-2">
            {t('Plan your clients sign up to')}
          </label>
          <div className="flex gap-2 flex-wrap">
            {PLANS.map((p, i) => (
              <button
                key={p.label}
                onClick={() => setPlanIdx(i)}
                className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${
                  planIdx === i
                    ? 'bg-brand-600 border-brand-500 text-white'
                    : 'bg-ink-900 border-ink-700 text-ink-100 hover:border-brand-500/50'
                }`}
              >
                {t(p.label)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="label block mb-2">
            {t('Deals you close per month:')} <span className="text-white font-semibold">{dealsPerMonth}</span>
          </label>
          <input
            type="range"
            min={1}
            max={20}
            value={dealsPerMonth}
            onChange={(e) => setDealsPerMonth(Number(e.target.value))}
            className="w-full accent-brand-500"
          />
          <div className="flex justify-between text-xs text-ink-500 mt-1">
            <span>{t('1 deal')}</span><span>{t('20 deals')}</span>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-ink-900 border border-ink-800 p-4 text-center">
          <div className="text-2xl font-bold text-white">€{(comm.setup * dealsPerMonth).toFixed(0)}</div>
          <div className="text-xs text-ink-500 mt-1">{t('setup fees upfront')}</div>
        </div>
        <div className="rounded-xl bg-ink-900 border border-ink-800 p-4 text-center">
          <div className="text-2xl font-bold text-brand-400">€{monthly.toFixed(0)}</div>
          <div className="text-xs text-ink-500 mt-1">{t('per month recurring')}</div>
        </div>
        <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">€{total6m.toFixed(0)}</div>
          <div className="text-xs text-ink-500 mt-1">{t('total over 6 months')}</div>
        </div>
      </div>

      <p className="text-xs text-ink-500 mt-4">
        {t('Per-client breakdown:')} €{comm.setup} {t('setup')} + €{comm.monthly.toFixed(2)}/mo × {COMMISSION.recurringMonths}mo = <span className="text-emerald-400 font-medium">€{comm.total6m} {t('per signup')}</span>.
      </p>
    </div>
  )
}

export default function HowItWorks() {
  const { t } = useI18n()
  const pro = calcCommission(59)
  return (
    <section className="section py-12 max-w-3xl">
      <h1 className="text-3xl sm:text-4xl font-bold text-white">{t('How partners earn money')}</h1>
      <p className="text-ink-500 mt-2">{t('The exact numbers. No hidden clauses.')}</p>

      <Link to="/story" className="card mt-6 flex items-center gap-4 border-brand-500/30 hover:border-brand-500/60 transition-colors">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-brand-600 text-lg text-white">▶</span>
        <span>
          <span className="block font-semibold text-white">{t('Rather watch than read?')}</span>
          <span className="block text-sm text-ink-500">{t('45-second animated story: one demo, one deal, six months of commission — live.')}</span>
        </span>
      </Link>

      <div className="card mt-8">
        <h2 className="text-xl font-semibold text-white">{t('Commission formula')}</h2>
        <ul className="mt-4 space-y-2 text-ink-100">
          <li>• <span className="text-emerald-400 font-semibold">€{COMMISSION.setupFee}</span> {t('one-time setup fee, paid by the business at signup —')} <span className="text-emerald-400">{t('100% of it goes to you, the referrer')}</span>.</li>
          <li>• <span className="text-emerald-400 font-semibold">{COMMISSION.recurringPercent}%</span> {t('of their monthly subscription, every month, for')} <span className="text-emerald-400">{COMMISSION.recurringMonths} {t('months')}</span>.</li>
          <li>• {t('After 6 months the customer continues paying us directly; you have been rewarded for bringing them in.')}</li>
        </ul>
      </div>

      <EarningsCalculator />

      <div className="card mt-5">
        <h2 className="text-xl font-semibold text-white">{t('Example: Pro plan, €59/month')}</h2>
        <table className="w-full text-sm mt-4">
          <tbody className="divide-y divide-ink-800">
            <Line label={t('Setup fee (you)')} value={`€${pro.setup.toFixed(2)}`} />
            <Line label={`${t('Monthly')} × ${pro.months}`} value={`€${pro.monthly.toFixed(2)} × ${pro.months}`} />
            <Line label={t('Total from one client')} value={`€${pro.total6m.toFixed(2)}`} highlight />
          </tbody>
        </table>
        <p className="text-ink-500 text-xs mt-4">
          {t('Do 5 Pro-plan signups in a month →')} €{(pro.total6m * 5).toFixed(2)} {t('earned over 6 months. Do 10 →')} €{(pro.total6m * 10).toFixed(2)}.
        </p>
      </div>

      <div className="card mt-5 border-amber-500/30">
        <h2 className="text-xl font-semibold text-white">{t('🌐 Bonus: custom-domain add-on')}</h2>
        <p className="text-ink-100 mt-2">
          {t('On the Business plan, the client can tick an optional')} <span className="text-amber-400 font-semibold">€{calcDomainCommission().price}</span> {t('add-on for a custom domain + branded setup (their own')} <span className="font-mono">yoursite.mk</span>).
          {' '}{t('You earn an instant')} <span className="text-emerald-400 font-semibold">€{calcDomainCommission().referrer}</span> {t('on top of everything else — no waiting, no 6-month drip.')}
        </p>
        <p className="text-ink-500 text-xs mt-3">
          {t('Example: Business plan (€99/mo) + domain add-on =')} <span className="text-ink-100">€30 {t('setup')} + €17.82/mo × 6 + €{calcDomainCommission().referrer} {t('domain')} = <span className="text-emerald-400 font-bold">€{(30 + 17.82 * 6 + calcDomainCommission().referrer).toFixed(2)}</span></span> {t('per signup')}.
        </p>
      </div>

      <div className="card mt-5">
        <h2 className="text-xl font-semibold text-white">{t('How the money reaches you')}</h2>
        <ol className="mt-4 space-y-2 text-ink-100 list-decimal list-inside">
          <li>{t('Business signs up → pays')} €{COMMISSION.setupFee} {t('setup by card.')}</li>
          <li>{t('You see the lead in your dashboard instantly. Status:')} <span className="chip bg-sky-500/10 text-sky-400 border border-sky-500/20">{t('new')}</span></li>
          <li>{t('We close & onboard the client. Status →')} <span className="chip bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{t('won')}</span></li>
          <li>{t('On the 15th of every month we pay out pending commissions via bank transfer / PayPal / Revolut.')}</li>
          <li>{t('Paid amounts appear as')} <span className="chip bg-brand-500/10 text-brand-500 border border-brand-500/20">{t('paid')}</span> {t('in your dashboard.')}</li>
        </ol>
      </div>

      <div className="card mt-5">
        <h2 className="text-xl font-semibold text-white">{t('What we already have ready')}</h2>
        <p className="text-ink-500 mt-1">{t('You can start showing these tomorrow. No waiting.')}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {PRODUCTS.filter((p) => p.demoUrl).map((p) => (
            <a
              key={p.slug}
              href={p.demoUrl!}
              target="_blank"
              rel="noopener noreferrer"
              className="chip bg-ink-800 text-ink-100 border border-ink-800 hover:border-brand-500/50"
            >
              {p.icon} {p.name} ↗
            </a>
          ))}
        </div>
      </div>

      <div className="card mt-5">
        <h2 className="text-xl font-semibold text-white">{t('💬 Pitch script (word for word)')}</h2>
        <p className="text-ink-500 text-sm mt-1">{t('Use this on any local business owner. Works in-person or via WhatsApp.')}</p>
        <div className="mt-4 rounded-xl bg-ink-900 border border-ink-800 p-4 text-sm text-ink-100 space-y-3">
          <p><span className="text-ink-500 text-xs uppercase tracking-wide">{t('Opening (30 sec)')}</span><br />
            {t('"Hi, I partner with a software company that builds AI booking systems for businesses like yours. I can show you a live demo on my phone — takes 2 minutes, no commitment."')}</p>
          <p><span className="text-ink-500 text-xs uppercase tracking-wide">{t('After the demo')}</span><br />
            {t('"The system books appointments, reminds clients automatically, and handles [their specific pain: missed calls / no-shows / paper scheduling]. It starts at €29/month. I handle the intro — the team takes it from there."')}</p>
          <p><span className="text-ink-500 text-xs uppercase tracking-wide">{t('Close')}</span><br />
            {t('"I just need your name and the business name to submit your interest. You pay nothing today — the setup fee is only charged once they decide to go ahead."')}</p>
        </div>
        <p className="text-xs text-ink-500 mt-3">{t('Then log into your dashboard →')} <span className="text-brand-500">{t('Submit a lead')}</span> {t('→ fill in the business name, city, and the product you showed them.')}</p>
      </div>

      <div className="mt-10 text-center">
        <Link to="/signup" className="btn-primary">{t('Join as partner — free')}</Link>
      </div>
    </section>
  )
}

function Line({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <tr>
      <td className="py-3 text-ink-100">{label}</td>
      <td className={`py-3 text-right ${highlight ? 'text-emerald-400 font-bold text-lg' : 'text-ink-100'}`}>{value}</td>
    </tr>
  )
}
